import { inject, Injectable } from "@angular/core";
import { Observable, map, of } from "rxjs";
import { AUTH_KEYS } from "../constants/auth-keys";
import { ROUTES } from "../constants/routes";
import { STORAGE_KEYS } from "../constants/storage-keys";
import { decodeJwtPayload, isJwtExpired } from "../helpers/token.helper";
import { UserClaims } from "../models/auth/user-claims.model";
import { LoginResponse } from "../models/account/login.model";
import { AccountService } from "./account.service";

@Injectable({ providedIn: "root" })
export class AuthService {
  private readonly accountService = inject(AccountService);

  ensureAuthenticated(): Observable<boolean> {
    if (typeof localStorage === "undefined") {
      return of(false);
    }

    const accessToken = this.getAccessToken();
    const refreshToken = this.getRefreshToken();

    if (!accessToken || !refreshToken) {
      this.clearSession();
      return of(false);
    }

    const payload = decodeJwtPayload(accessToken);
    if (!payload) {
      this.clearSession();
      return of(false);
    }

    if (!isJwtExpired(accessToken)) {
      return of(true);
    }

    return this.accountService.refreshToken({ accessToken, refreshToken }).pipe(
      map(response => {
        if (response.isSuccess && response.data.accessToken && response.data.refreshToken) {
          this.storeTokens(response.data);
          return true;
        }

        this.clearSession();
        return false;
      })
    );
  }

  storeTokens(response: LoginResponse): void {
    if (!response.accessToken || !response.refreshToken) {
      return;
    }

    this.setAccessToken(response.accessToken);
    this.setRefreshToken(response.refreshToken);
  }

  getClaims(): UserClaims | null {
    const accessToken = this.getAccessToken();
    if (!accessToken) {
      return null;
    }

    return UserClaims.fromToken(accessToken);
  }

  getAccessToken(): string | null {
    if (typeof localStorage === "undefined") {
      return null;
    }

    return localStorage.getItem(STORAGE_KEYS.accessToken);
  }

  getRefreshToken(): string | null {
    if (typeof localStorage === "undefined") {
      return null;
    }

    return localStorage.getItem(STORAGE_KEYS.refreshToken);
  }

  isAuthenticated(): boolean {
    const accessToken = this.getAccessToken();
    if (!accessToken) {
      return false;
    }

    return !isJwtExpired(accessToken) && decodeJwtPayload(accessToken) !== null;
  }

  setReturnUrl(url: string): void {
    if (typeof sessionStorage === "undefined") {
      return;
    }

    sessionStorage.setItem(AUTH_KEYS.returnUrl, url);
  }

  getReturnUrl(): string | null {
    if (typeof sessionStorage === "undefined") {
      return null;
    }

    return sessionStorage.getItem(AUTH_KEYS.returnUrl);
  }

  clearReturnUrl(): void {
    if (typeof sessionStorage === "undefined") {
      return;
    }

    sessionStorage.removeItem(AUTH_KEYS.returnUrl);
  }

  consumeReturnUrl(fallback?: string): string {
    const returnUrl = this.getReturnUrl() || fallback || this.getRoleBasedHomeUrl();
    this.clearReturnUrl();
    return returnUrl;
  }

  getRoleBasedHomeUrl(): string {
    const role = this.getClaims()?.role;
    return role === "Admin" ? ROUTES.adminFiles.url : ROUTES.dashboardBulkVerification.url;
  }

  clearSession(): void {
    if (typeof localStorage === "undefined") {
      return;
    }

    localStorage.removeItem(STORAGE_KEYS.accessToken);
    localStorage.removeItem(STORAGE_KEYS.refreshToken);
  }

  private setAccessToken(token: string): void {
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(STORAGE_KEYS.accessToken, token);
    }
  }

  private setRefreshToken(token: string): void {
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(STORAGE_KEYS.refreshToken, token);
    }
  }
}
